import { Buffer } from 'buffer';
import styled from '@emotion/styled';
import { Clear } from '@mui/icons-material';
import { LoadingButton, TabContext } from '@mui/lab';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Tabs,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { deflate } from 'pako';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useFetch } from 'use-http';
import ArrowIcon from 'assets/icons/arrow-down.svg';
import { InfoBox, InfoWrapper, RHFMultiSelect, RHFSelect } from 'components';
import { CardL2 } from 'components/TabsL2/CardL2/CardL2';
import { erosioneL3DangerFields } from 'components/TabsL2/erosioneL3DangerFields';
import {
  CdATag,
  ContainerCard,
  SecondTabIdraulica,
  TabPanelStyle,
  WrapperCardL2,
} from 'components/TabsL2/TabsL2.style';
import { apiBridges } from 'constants/api';
import { colors, InspectionStatusesMap, L2CdAMap } from 'constants/colors';
import {
  CondizioniTransitabilitaL3Options,
  EsitoValutazioneL3Options,
} from 'constants/selectOptions';
import { useInspectionPath } from 'hooks';
import { INFO } from 'pages/Inspections/config/info';
import { useInspectionSelector } from 'stores';
import { mappingDataOutL3 } from '../utils/mappingOutL3';

// Colore del bordo a righe diagonali per singolo parametro, stessa mappa
// locale, non esportata, usata in TabsL2.jsx per il rischio idraulico L2
// (il valore va da 1 a 8).
const mappingColorBorder = {
  1: colors.WILDWILLOW,
  2: colors.SPROUT,
  3: colors.CREAMBRULEE,
  4: colors.SUNGLOW,
  5: colors.MAUVELOUS,
  6: colors.SILVER,
  7: colors.SILVER,
  8: colors.SILVER,
};

// marginTop/marginLeft allineati a CardContainer (CardL2.style.js:
// marginTop 10px, marginLeft 10) così l'etichetta parte esattamente sopra
// il bordo sinistro dei box sottostanti.
const SectionHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 10,
  marginLeft: 10,
  marginBottom: 4,
});

const ColumnBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
});

const CardsRow = styled(Box)({
  display: 'flex',
});

// Estende WrapperCardL2 (componente condiviso con la CdA idraulica L2,
// TabsL2.style.js) con un marginTop locale — non si tocca lo styled
// originale per non alterare il rendering di L2.
const WrapperCardL2WithMargin = styled(WrapperCardL2)({
  marginTop: 16,
});

// Sfondo bianco esplicito: l'accordion L3 (AccordionCustom.style.jsx,
// AccordionContainer) ha backgroundColor colors.LIGHTGREY — lo stesso
// grigio di CardContainer (CardL2.style.js) — quindi le card si confondono
// con lo sfondo senza questo override. In TabsL2.jsx ogni Accordion imposta
// backgroundColor WHITE via style inline per lo stesso motivo.
const WhitePanel = styled(Box)({
  backgroundColor: colors.WHITE,
  padding: 16,
});

const RecalculateToolbar = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: 8,
});

// Stessa styled component (nome e regole identiche) di TabsL2.jsx, non
// esportata da lì — replicata qui per l'header "CdA IDRAULICA" (chevron via
// backgroundImage sull'icona <Clear/>, badge classe a destra via
// justifyContent space-between).
const NestedAccordionSummary = styled(AccordionSummary)({
  '& .MuiAccordionSummary-content': {
    justifyItems: 'center',
    justifyContent: 'space-between',
  },
  '& svg': {
    backgroundImage: `url(${ArrowIcon})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    fill: 'none',
    backgroundSize: '60%',
  },
});

const UnsavedChangesWarning = styled(Box)({
  marginBottom: 8,
});

// Issue #1021, punti 15a/15b — stesso marginLeft di SectionHeader (allineato
// al bordo sinistro dei box sottostanti, vedi commento su SectionHeader
// sopra); marginTop maggiore per staccarlo dall'ultima card sopra (Esposizione
// per il 15b, Pericolosità per il 15a) invece di restare attaccato subito
// sotto. Condiviso fra i due avvisi (stesso posizionamento, colore diverso).
const SectionWarningWrapper = styled(Box)({
  marginTop: 16,
  marginLeft: 10,
  marginRight: 10,
});

// Le 4 card dell'Esposizione (Rilevanza ente scavalcato → Alternative
// stradali → Frequente passaggio persone → TGM/Luce media campate) sono
// qui SORELLE allo stesso livello (invece di annidate via childrenArray di
// CardL2, come fa TabsL2.jsx per L2) — childrenArray genera un div
// wrapper per ogni livello e, non potendo intervenire su CardL2.jsx
// (condiviso con L2), l'allineamento a sinistra tra i livelli non è
// controllabile da qui. Come card sorelle, ognuna è un CardContainer
// diretto con lo stesso marginLeft, garantendo lo stesso punto di partenza
// per tutte. Il "borderpx" decrescente (13/9/7/5, default incluso) resta
// per il solo effetto visivo di rastrematura della riga colorata.
//
// L'oggetto exposure passato qui non arriva più dall'endpoint CdA di L2
// (bridges/inspections/:id/cda) ma è già isolato dentro
// diagnosis.L3.cdaGraphicalRepresentation.{overlapCda,erosionCda}.exposure
// (stesso oggetto condiviso per i due fenomeni — vedi
// InspectionExtensions.calculateHydraulicCdaGraphicalRepresentationL3,
// apps/api/.../inspection.extensions.ts): stessa forma di sempre
// (section.affollamentoEfunzioniEnteScavalcato/section.section...), quindi
// leggiamo i campi direttamente invece di passare da mappingL2 (che si
// aspetta l'intero albero cda di L2, non più disponibile qui).
const renderExposureCascade = exposure => (
  <CardsRow>
    <ColumnBox>
      <CardL2
        sectionColorCdA={L2CdAMap[exposure?.class]?.labelColor}
        color={mappingColorBorder[exposure?.section?.value]}
        values={[
          {
            label: 'Rilevanza ente scavalcato',
            value: exposure?.section?.affollamentoEfunzioniEnteScavalcato,
          },
        ]}
      />
      <CardL2
        color={mappingColorBorder[exposure?.section?.section?.value]}
        values={[
          {
            label: 'Alternative stradali',
            value: exposure?.section?.section?.alternativeStradali,
          },
        ]}
        borderpx="9px"
      />
      <CardL2
        color={mappingColorBorder[exposure?.section?.section?.section?.value]}
        values={[
          {
            label: 'Frequente passaggio persone',
            value:
              exposure?.section?.section?.section?.frequentePassaggioPersone,
          },
        ]}
        borderpx="7px"
      />
      <CardL2
        color={
          mappingColorBorder[
            exposure?.section?.section?.section?.section?.value
          ]
        }
        values={[
          {
            label: 'TGM (carreggiata)',
            value:
              exposure?.section?.section?.section?.section
                ?.valoreApprossimativoTGM,
          },
          {
            label: 'Luce media campate',
            value:
              exposure?.section?.section?.section?.section?.luceMediaCampate,
          },
        ]}
        borderpx="5px"
      />
    </ColumnBox>
    <CardL2
      color="transparent"
      values={[
        {
          label: "Strategicità dell'opera",
          value: exposure?.strategicity,
        },
      ]}
    />
  </CardsRow>
);

// Issue #1021, punto 15b — quando il fenomeno è calcolato con l'algoritmo L3
// (presente in analisiFenomenoAnalizzato, ID577) i campi di Esposizione
// mostrati qui sono comunque ereditati dall'ultima ispezione L1 (sharedExposure
// nel calcolo BE, vedi commento su renderExposureCascade sotto), non
// ricalcolati per l'attività L3 stessa — vanno segnalati come tali.
const L3_EXPOSURE_INHERITED_WARNING =
  'Campi estratti da ultima ispezione L1. Per modifica, creare nuova attività L0,1';

// Issue #1021, punto 15a — caso opposto al 15b: quando il fenomeno NON è
// presente in analisiFenomenoAnalizzato (ID577), la Pericolosità mostrata
// non è calcolata con l'algoritmo L3 ma con la logica/matrice tradizionale
// di Livello 2. Testo NON fornito dal cliente nell'issue (a differenza del
// 15b, che ha il testo esatto) — placeholder da confermare.
const L3_DANGER_L2_ALGORITHM_WARNING =
  "Fenomeno calcolato secondo l'algoritmo di Livello 2 (matrice tradizionale), non con l'algoritmo L3. Per usare l'algoritmo L3 selezionare il fenomeno in 'Analisi L3 - Fenomeno analizzato'.";

// Issue #1021, riapertura 2026-09-25, punto 9.1 — elenchi ESPLICITI (non più
// lettura dinamica delle chiavi di `vulnerability`, vedi sotto per il
// perché), uno per fenomeno, ordine e campi presi 1:1 dalle classi BE
// (apps/api/src/inspections/models/diagnosis.ts: OverlapCdaVulnerability
// riga ~4564, GeneralizedErosionCdaVulnerability riga ~5037,
// LocalizedErosionCdaVulnerability riga ~5294) — non dal contratto
// cda-idraulica-mista-l2-l3.md, che per sua stessa ammissione documenta
// questi campi con "…" (non esaustivo).
//
// Issue #1021, punto (segnalazione cliente 2026-09-25 successiva): la prima
// versione di questa colonna leggeva le chiavi di `vulnerability`
// dinamicamente (Object.entries) — un campo NON compilato (undefined) veniva
// però eliminato da JSON.stringify prima ancora di arrivare al FE (proprietà
// undefined non serializzate), quindi spariva silenziosamente dalla lista
// invece di comparire come card vuota/rossa "Dati non sufficienti" (comportamento
// di CardL2.jsx per qualunque valore assente, coerente con le colonne
// Pericolosità/Esposizione). Da qui la segnalazione "manca Evidenze sulla
// profondità delle fondazioni" quando quel campo specifico non era compilato
// sull'attività di test. Liste esplicite, lette con `?.`, risolvono alla
// radice: un campo non compilato resta undefined ma la sua card resta
// visibile.
const sormontoVulnerabilityFields = overlapCda => [
  {
    label: 'Superficie presunta [km2]', // 115
    value: overlapCda?.vulnerability?.bacinoIdrograficoPresuntoRischioIdraulico,
  },
  {
    label: 'Evidente e accentuata deposizione di sedimenti', // 145
    value: overlapCda?.vulnerability?.deposizioneSedimenti,
  },
  {
    label: 'Trasporto di materiale vegetale di notevole dimensione', // 147
    value: overlapCda?.vulnerability?.materialeVegetaleRischioIdraulico,
  },
];

// abbassamentoAccentuatoAlleFondazioni (ID163) è rilevante solo quando
// "Evidenze sulla profondità delle fondazioni" = Profonde/Su roccia E
// "Abbassamento generalizzato dell'alveo" = Si (diagnosis.ts, setFVector/
// setIVector) — stesso pattern "se presente" già in uso per Ca/Cg
// (erosioneGeneralizzataDangerFields), filtrato quando non applicabile
// invece di mostrato sempre vuoto. Issue #1021, correzione ricevuta dopo la
// prima versione di questa funzione: il filtro va applicato SOLO a questo
// campo condizionale, mai agli altri — un `.filter()` sull'intero array (bug
// della prima versione) toglieva silenziosamente anche i campi FISSI non
// ancora compilati (es. evidenzeProfonditaDelleFondazioni), che invece
// devono sempre comparire come card vuota "Dati non sufficienti" quando non
// compilati, non sparire.
const erosioneGeneralizzataVulnerabilityFields = generalizedErosionCda => {
  const abbassamentoAccentuato =
    generalizedErosionCda?.vulnerability?.abbassamentoAccentuatoAlleFondazioni;
  return [
    {
      label: 'Evidenze sulla profondità delle fondazioni', // 164
      value: generalizedErosionCda?.vulnerability?.evidenzeProfonditaDelleFondazioni,
    },
    {
      label: "Abbassamento generalizzato dell'alveo", // 161
      value: generalizedErosionCda?.vulnerability?.abbassamentoGeneralizzatoAlveo,
    },
    {
      label: 'Alveo avente sensibile curvatura in corrispondenza del ponte', // 116
      value: generalizedErosionCda?.vulnerability?.curvaturaAlveoRischioIdraulico,
    },
    ...(abbassamentoAccentuato !== undefined && abbassamentoAccentuato !== null
      ? [
          {
            label:
              'Abbassamento molto accentuato anche in relazione alle fondazioni', // 163
            value: abbassamentoAccentuato,
          },
        ]
      : []),
  ];
};

// abbassamentoAccentuatoAlleFondazioni (ID163) — stesso campo condizionale
// di erosioneGeneralizzataVulnerabilityFields sopra, stesso filtro mirato
// (solo su questo campo, mai sugli altri — vedi commento sopra).
const erosioneLocalizzataVulnerabilityFields = localizedErosionCda => {
  const abbassamentoAccentuato =
    localizedErosionCda?.vulnerability?.abbassamentoAccentuatoAlleFondazioni;
  return [
    {
      label: 'Evidenze sulla profondità delle fondazioni', // 164
      value: localizedErosionCda?.vulnerability?.evidenzeProfonditaDelleFondazioni,
    },
    {
      label: "Abbassamento generalizzato dell'alveo", // 161
      value: localizedErosionCda?.vulnerability?.abbassamentoGeneralizzatoAlveo,
    },
    {
      label: 'Accumuli di detriti o materiale flottante a monte della pila', // 166
      value: localizedErosionCda?.vulnerability?.accumuliDetritiAMontePila,
    },
    {
      label: "Tendenza dell'alveo a divagazione planimetrica", // 126
      value: localizedErosionCda?.vulnerability?.divagazioneAlveoRischioIdraulico,
    },
    {
      label: 'Protezione al piede delle pile e delle spalle del ponte', // 171
      value: localizedErosionCda?.vulnerability?.protezioneAlPiedePileSpalle,
    },
    {
      label: 'Briglia di protezione immediatamente a valle del ponte', // 174
      value: localizedErosionCda?.vulnerability?.brigliaProtezioneValleDelPonte,
    },
    ...(abbassamentoAccentuato !== undefined && abbassamentoAccentuato !== null
      ? [
          {
            label:
              'Abbassamento molto accentuato anche in relazione alle fondazioni', // 163
            value: abbassamentoAccentuato,
          },
        ]
      : []),
  ];
};

// Colonna Pericolosità + colonna Vulnerabilità (solo ramo L2) + colonna
// Esposizione per un fenomeno (Sormonto, oppure Erosione
// fusa/generalizzata/localizzata) — stessa struttura
// WrapperCardL2WithMargin/ColumnBox/SectionHeader per le tre colonne,
// dangerFields è l'array `values` della sola card Pericolosità (varia per
// fenomeno), vulnerabilityFields è l'equivalente per la card Vulnerabilità
// (omesso/undefined nel ramo L3, dove la vulnerabilità non esiste — vedi
// cda-idraulica-mista-l2-l3.md), l'Esposizione è sempre
// `renderExposureCascade`. isL3Calculated: true solo quando QUESTO
// specifico pannello rappresenta il fenomeno calcolato con l'algoritmo L3
// (non quando true per l'altro fenomeno) — va passato esplicitamente da
// ogni chiamata (derivato da `calcMethod`, non da analisiFenomenoAnalizzato
// — vedi cda-idraulica-mista-l2-l3.md), non dedotto qui dentro.
const renderPericolositaEsposizionePanel = (
  cdaGraphicalRepresentation,
  phenomenonCda,
  dangerFields,
  isL3Calculated,
  vulnerabilityFields
) => (
  <>
    {/* Issue #1021, punto 15a — banner a piena larghezza, sopra TUTTE le
    colonne (non solo dentro la colonna Pericolosità): riguarda il pannello
    nel suo complesso, non un singolo campo. */}
    {!isL3Calculated && (
      <SectionWarningWrapper>
        <InfoBox
          color={colors.LIGHTRED}
          text={L3_DANGER_L2_ALGORITHM_WARNING}
        />
      </SectionWarningWrapper>
    )}
    <ContainerCard>
      {/* Issue #1021, riapertura 2026-09-25, punto 9.2 — screenshot cliente:
      la barra laterale colorata (prop `section`, borderLeft) non deve
      comparire quando il titolo è "Condizioni" (ramo L3), solo quando è
      "Pericolosità" (ramo L2) — stesso criterio già usato per il badge
      CdATag qui sotto (`!isL3Calculated`), ora esteso al bordo. */}
      <WrapperCardL2WithMargin
        section={isL3Calculated ? undefined : phenomenonCda?.class}
      >
        <ColumnBox>
          <SectionHeader>
            <Typography variant="h3" sx={{ fontSize: 16, fontWeight: 'bold' }}>
              {isL3Calculated ? 'Condizioni' : 'Pericolosità'}
            </Typography>
            {!isL3Calculated && (
              <CdATag
                cda={cdaGraphicalRepresentation}
                cdaSectionData={phenomenonCda?.class}
              >
                <Typography variant="h3" sx={{ fontSize: 14 }}>
                  {L2CdAMap[phenomenonCda?.class]?.labelText ?? 'N.D.'}
                </Typography>
              </CdATag>
            )}
          </SectionHeader>
          <CardsRow>
            <CardL2
              sectionColorCdA={L2CdAMap[phenomenonCda?.class]?.labelColor}
              color="transparent"
              values={dangerFields}
            />
          </CardsRow>
        </ColumnBox>
      </WrapperCardL2WithMargin>
      {/* Issue #1021, riapertura 2026-09-25, punto 9.1 — colonna
      Vulnerabilità, solo ramo L2 (vulnerabilityFields passato dal chiamante
      solo in quel caso): badge/bordo letti da `phenomenonCda?.vulnerability`,
      stesso pattern delle altre due colonne. */}
      {vulnerabilityFields && (
        <WrapperCardL2WithMargin section={phenomenonCda?.vulnerability?.class}>
          <ColumnBox>
            <SectionHeader>
              <Typography
                variant="h3"
                sx={{ fontSize: 16, fontWeight: 'bold' }}
              >
                Vulnerabilità
              </Typography>
              <CdATag
                cda={cdaGraphicalRepresentation}
                cdaSectionData={phenomenonCda?.vulnerability?.class}
              >
                <Typography variant="h3" sx={{ fontSize: 14 }}>
                  {L2CdAMap[phenomenonCda?.vulnerability?.class]?.labelText ??
                    'N.D.'}
                </Typography>
              </CdATag>
            </SectionHeader>
            <CardsRow>
              <CardL2
                sectionColorCdA={
                  L2CdAMap[phenomenonCda?.vulnerability?.class]?.labelColor
                }
                color="transparent"
                values={vulnerabilityFields}
              />
            </CardsRow>
          </ColumnBox>
        </WrapperCardL2WithMargin>
      )}
      {/* Issue #1021, riapertura 2026-09-25, punto 9 — il colore laterale
      (prop `section`, borderLeft di WrapperCardL2 — TabsL2.style.js) deve
      riflettere il risultato dell'ESPOSIZIONE, non quello del fenomeno
      (Sormonto/Erosione) come sopra: stesso principio già corretto usato
      dal badge numerico (CdATag) qui sotto, che legge
      `phenomenonCda?.exposure?.class`, non `phenomenonCda?.class`. */}
      <WrapperCardL2WithMargin section={phenomenonCda?.exposure?.class}>
        <ColumnBox>
          <SectionHeader>
            <Typography variant="h3" sx={{ fontSize: 16, fontWeight: 'bold' }}>
              Esposizione
            </Typography>
            <CdATag
              cda={cdaGraphicalRepresentation}
              cdaSectionData={phenomenonCda?.exposure?.class}
            >
              <Typography variant="h3" sx={{ fontSize: 14 }}>
                {L2CdAMap[phenomenonCda?.exposure?.class]?.labelText ?? 'N.D.'}
              </Typography>
            </CdATag>
          </SectionHeader>
          {renderExposureCascade(phenomenonCda?.exposure)}
          {isL3Calculated && (
            <SectionWarningWrapper>
              <InfoBox
                color="warning.light"
                text={L3_EXPOSURE_INHERITED_WARNING}
              />
            </SectionWarningWrapper>
          )}
        </ColumnBox>
      </WrapperCardL2WithMargin>
    </ContainerCard>
  </>
);

// SORMONTO — ramo L3 (invariato): un solo campo di Pericolosità, in cima.
const sormontoL3DangerFields = overlapCda => [
  {
    label: 'Franco idraulico FP2 [m]',
    value: overlapCda?.danger?.francoIdraulicoFp2,
  },
];

// SORMONTO — ramo L2 (fallback, nuovo — cda-idraulica-mista-l2-l3.md):
// francoIdraulicoFp2 è annidato sotto `danger.section1` in questo ramo, non
// in cima come nel ramo L3. Issue #1021, riapertura 2026-09-24 — allineato
// al ramo L3 (sormontoL3DangerFields sopra): solo Franco idraulico FP2 nella
// colonna Pericolosità, "Fenomeno Sormonto (rischio idraulico)" non più
// mostrato. La vulnerabilità (rimossa nello stesso giro) è stata
// reintrodotta come colonna a sé — vedi vulnerabilityDangerFields, punto 9.1.
const sormontoL2DangerFields = overlapCda => [
  {
    label: 'Franco idraulico FP2 [m]',
    value: overlapCda?.danger?.section1?.francoIdraulicoFp2,
  },
];

// EROSIONE — ramo L2, generalizzata (matrice tradizionale Ca/Cg, annidati
// sotto generalizedErosionCda.danger). Issue #1021, riapertura 2026-09-24 —
// solo Ca e Cg nella colonna Pericolosità, "Fenomeno Erosione (rischio
// idraulico)" non più mostrato; ciascuno dei due "se presente" (filtrati
// quando mancante, non mostrati con lo stile "dati insufficienti" di
// CardL2.jsx). Vulnerabilità: vedi commento su sormontoL2DangerFields sopra.
const erosioneGeneralizzataDangerFields = generalizedErosionCda =>
  [
    {
      label: "Fattore di restringimento dell'alveo inciso Ca [%]",
      value: generalizedErosionCda?.danger?.ca,
    },
    {
      label: 'Fattore di restringimento delle aree golenali Cg [%]',
      value: generalizedErosionCda?.danger?.cg,
    },
  ].filter(field => field.value !== undefined && field.value !== null);

// EROSIONE — ramo L2, localizzata: IEL realmente calcolato dal BE
// (`fattoreIEL`). Issue #1021, riapertura 2026-09-24 — solo IEL nella
// colonna Pericolosità, "Fenomeno Erosione (rischio idraulico)" non più
// mostrato. Vulnerabilità: vedi commento su sormontoL2DangerFields sopra.
const erosioneLocalizzataDangerFields = localizedErosionCda => [
  { label: 'IEL', value: localizedErosionCda?.danger?.fattoreIEL },
];

// Riepilogo CdA idraulica: tab cliccabili SORMONTO/EROSIONE (stessa logica
// di TabsL2.jsx/nestedTabRischioIdraulico), ciascuna con Pericolosità ed
// Esposizione. Issue #1021, riapertura 2026-09-24 — Sormonto/Erosione
// generalizzata/Erosione localizzata (rami L2) mostrano solo i campi
// richiesti dal cliente (vedi commenti sulle singole funzioni *DangerFields
// sopra): niente più "Fenomeno ... (rischio idraulico)" né vulnerabilità.
// Dati letti da
// diagnosis.L3.cdaGraphicalRepresentation (campo BE, vedi
// InspectionExtensions.calculateHydraulicCdaGraphicalRepresentationL3), già
// presente nel form L3 (mappingInL3.js fa uno spread diretto di
// diagnosis.L3) — nessuna chiamata separata all'endpoint CdA di L2.
//
// Issue #1021, punti 9/15a — dal 2026-08-31 (e-2026-06-t29,
// apps/api/docs/cda-idraulica-mista-l2-l3.md) il BE calcola e restituisce
// ANCHE il fenomeno non scelto in "Analisi L3 - Fenomeno analizzato" (577),
// con l'algoritmo L2 tradizionale (dati reali, non più `N.D.`/`_mock`), più
// una `class` complessiva già aggregata. La regola di rendering è UNA SOLA,
// per fenomeno: guarda `calcMethod` ('L3'|'L2') su overlapCda/erosionCda —
// mai dedurlo da analisiFenomenoAnalizzato, che riguarda la SCELTA
// dell'utente, non quale algoritmo ha effettivamente prodotto i dati
// mostrati (i due possono disallinearsi solo per un attimo tra un cambio di
// 577 e il successivo "Ricalcola CdA"/salvataggio). Il tab Sormonto è quindi
// SEMPRE renderizzato con dati reali (niente più il placeholder "non
// selezionato per l'analisi L3"); per l'Erosione la struttura fusa/split
// delle tab segue lo stesso segnale (erosionCda.calcMethod), che nella
// pratica coincide 1:1 con la scelta 577 (l'algoritmo L2 esiste solo per il
// fenomeno che l'utente non ha scelto).
const CdaHydraulicSummaryL3 = () => {
  const { watch, getValues, setValue, formState } = useFormContext();
  // isDirty è dell'INTERO form L3 (Contesto dati generali, tutti gli
  // accordion), non solo dei campi Erosione letti sopra — un avviso
  // corretto anche per modifiche fatte in Accordion1/2/3. handleRecalculate
  // (preview, non persiste) non lo azzera di proposito; solo un salvataggio
  // riuscito lo fa (reset() in useInspectionL3.js onSubmit).
  const { isDirty } = formState;
  const { inspectionId } = useParams();
  // Task 18 — questo accordion è raggiungibile solo dentro un'attività L3
  // (getNewInspectionTabs, disabled: !isL3Activity), quindi inspectionDisabled
  // qui è già true quando il proprietario del ponte ha perso il modulo L3
  // (useInspection.js, isL3ActivityWithoutEditAccess) — nessun bisogno di
  // leggere l3Access separatamente. Vedi
  // apps/web-bo/plans/features/l3/task-18-l3-condivisione-handoff-fe.md.
  const { inspectionDisabled } = useInspectionSelector();
  // e-2026-06-t21 — "Ricalcola CdA" invia comunque una POST di preview: va
  // bloccato anche in sola lettura via rotta view (bottone "Approfondisci",
  // TabL3.jsx), non solo per permesso/entitlement, coerente con gli altri
  // campi di questo accordion e degli altri 1-4/6.
  const { isViewingInspection } = useInspectionPath();
  const { get, post } = useFetch();
  const { enqueueSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState(0);
  const [recalculating, setRecalculating] = useState(false);

  // Unico campo osservato: tutti i dati mostrati sotto (entrambi i rami
  // L3/L2 di entrambi i fenomeni) arrivano ora da qui, nessun altro campo
  // form serve più a questo componente (i vecchi campi Erosione letti dal
  // form RHF sono sostituiti dalla lettura diretta di erosionCda.danger,
  // vedi erosioneL3DangerFields sopra).
  const cdaGraphicalRepresentation = watch('cdaGraphicalRepresentation');

  // Ricalcolo on-demand della CdA L3 senza salvare: POST dedicata
  // (bridges.controller.ts previewL3Cda), stesso payload della PUT di
  // salvataggio (useInspectionL3.js onSubmit — diagnosis compresso via
  // deflate/base64, più status/completionPercentage/completionPercentageApp
  // e version, questi ultimi non nel form RHF quindi riletti al volo dalla
  // GET dell'ispezione). La risposta è già e solo
  // Level3.cdaGraphicalRepresentation (InspectionsService.
  // computeL3CdaGraphicalRepresentation) — si rimpiazza direttamente nel
  // form (mai inviato in salvataggio, mappingOutL3.js lo omette di
  // proposito) così i tab sopra si aggiornano subito.
  const handleRecalculate = async () => {
    setRecalculating(true);
    try {
      const inspection = await get(`${apiBridges.INSPECTIONS}/${inspectionId}`);
      const body = {
        diagnosis: mappingDataOutL3(getValues()),
        status: inspection?.status,
        completionPercentage: inspection?.completionPercentage,
        completionPercentageApp: inspection?.completionPercentageApp,
      };
      const compressedBody = deflate(JSON.stringify(body));
      const compressedBodyBuffer = Buffer.from(compressedBody);

      const newCdaGraphicalRepresentation = await post(
        `${apiBridges.INSPECTIONS}/${inspectionId}/l3/cda-preview`,
        {
          zip: compressedBodyBuffer.toString('base64'),
          version: inspection?.version,
        }
      );

      setValue('cdaGraphicalRepresentation', newCdaGraphicalRepresentation, {
        shouldDirty: false,
      });
      enqueueSnackbar('CdA idraulica ricalcolata', { variant: 'success' });
    } catch (err) {
      console.error('Error in L3 CdA preview: ', err);
      enqueueSnackbar('Si è verificato un errore nel ricalcolo della CdA', {
        variant: 'error',
      });
    } finally {
      setRecalculating(false);
    }
  };

  const overlapCda = cdaGraphicalRepresentation?.overlapCda;
  const erosionCda = cdaGraphicalRepresentation?.erosionCda;
  // Issue #1021, punto 9 — `class` complessiva ora calcolata e restituita
  // direttamente dal BE (cda-idraulica-mista-l2-l3.md): il vecchio
  // combineHydraulicCdaClassL3 client-side (rimosso) replicava la vecchia
  // regola BE che ignorava il fenomeno non calcolato in L3 invece di usarne
  // il valore L2 — nei casi misti dava un risultato sbagliato.
  const cdaIdraulicaClass = cdaGraphicalRepresentation?.class;

  const sormontoIsL3 = overlapCda?.calcMethod === 'L3';
  const erosioneIsL3 = erosionCda?.calcMethod === 'L3';
  const generalizedErosionCda = erosionCda?.generalizedErosionCda;
  const localizedErosionCda = erosionCda?.localizedErosionCda;

  // Etichetta di una tab (testo + badge classe CdA) — estratta per evitare
  // di avvolgere le tab Erosione condizionali in un Fragment: MUI <Tabs>
  // clona ogni figlio diretto per iniettargli selected/onClick/larghezza
  // fullWidth, ma un <>...</> con più tab dentro conta come UN SOLO figlio
  // (Fragment), quindi le tab al suo interno non vengono clonate/stilizzate
  // correttamente — risultato: non cliccabili e non dimensionate come le
  // altre. Le tab Erosione vengono quindi costruite come array piatto
  // (erosionTabs) e "spreadate" come children diretti di <Tabs>, mai
  // wrappate in un Fragment.
  const renderTabLabel = (text, sectionClass) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <p>{text}</p>
      <CdATag cda={cdaGraphicalRepresentation} cdaSectionData={sectionClass}>
        <Typography variant="h3" sx={{ fontSize: 14 }}>
          {L2CdAMap[sectionClass]?.labelText ?? 'N.D.'}
        </Typography>
      </CdATag>
    </div>
  );

  // Issue #1021, punto 9 — ogni sotto-erosione ha una `class` propria
  // (generalizedErosionCda/localizedErosionCda), diversa da erosionCda.class
  // (il padre, aggregato): il badge di ciascuna tab deve mostrare la
  // propria, non quella del padre (cda-idraulica-mista-l2-l3.md).
  const erosionTabsConfig = erosioneIsL3
    ? [{ label: 'EROSIONE', value: 1, class: erosionCda?.class }]
    : [
        {
          label: 'EROSIONE GENERALIZZATA',
          value: 1,
          class: generalizedErosionCda?.class,
        },
        {
          label: 'EROSIONE LOCALIZZATA',
          value: 2,
          class: localizedErosionCda?.class,
        },
      ];

  return (
    <Accordion
      defaultExpanded
      style={{
        backgroundColor: colors.WHITE,
        marginBottom: 10,
        boxShadow: 'none',
        borderRadius: 6,
        padding: 0,
        border: `3px solid ${
          L2CdAMap[cdaIdraulicaClass]?.labelColor ?? colors.GREY
        }`,
      }}
    >
      <NestedAccordionSummary expandIcon={<Clear />}>
        <Typography variant="h3" sx={{ fontSize: 16, marginTop: '8px' }}>
          CdA IDRAULICA
        </Typography>
        <CdATag
          cda={cdaGraphicalRepresentation}
          cdaSectionData={cdaIdraulicaClass}
        >
          <Typography variant="h3" sx={{ fontSize: 16 }}>
            {L2CdAMap[cdaIdraulicaClass]?.labelText ?? 'N.D.'}
          </Typography>
        </CdATag>
      </NestedAccordionSummary>
      <AccordionDetails style={{ padding: 0 }}>
        <WhitePanel>
          {isDirty && !inspectionDisabled && (
            <UnsavedChangesWarning>
              <InfoBox
                color="warning.light"
                text={
                  <>
                    {'Sono presenti '}
                    <b>{'modifiche non ancora salvate'}</b>
                    {
                      ' in uno o più campi della tab L3: la CdA Idraulica mostrata sotto potrebbe non riflettere questi valori. Usa '
                    }
                    <b>{'"Ricalcola CdA"'}</b>
                    {
                      ' per una anteprima aggiornata (non salva i dati) oppure salva per aggiornare definitivamente sia i dati che la CdA'
                    }
                  </>
                }
              />
            </UnsavedChangesWarning>
          )}
          <RecalculateToolbar>
            <LoadingButton
              variant="contained"
              color="primary"
              size="small"
              loading={recalculating}
              disabled={inspectionDisabled || isViewingInspection}
              onClick={handleRecalculate}
            >
              Ricalcola CdA
            </LoadingButton>
          </RecalculateToolbar>
          <TabContext value={activeTab}>
            <Tabs
              TabIndicatorProps={{ style: { backgroundColor: 'white' } }}
              value={activeTab}
              variant="fullWidth"
              onChange={(_, newValue) => setActiveTab(newValue)}
            >
              <SecondTabIdraulica
                key="sormonto"
                borderColor={mappingColorBorder[overlapCda?.class]}
                color={InspectionStatusesMap[overlapCda?.class]?.labelColor}
                label={renderTabLabel('SORMONTO', overlapCda?.class)}
                value={0}
              />
              {erosionTabsConfig.map(tab => (
                <SecondTabIdraulica
                  key={tab.value}
                  borderColor={mappingColorBorder[tab.class]}
                  color={InspectionStatusesMap[tab.class]?.labelColor}
                  label={renderTabLabel(tab.label, tab.class)}
                  value={tab.value}
                />
              ))}
            </Tabs>
            {/* Issue #1021, punti 9/15a — Sormonto è sempre renderizzato con
            dati reali: quando non scelto in 577, il BE restituisce il ramo
            L2 (fallback tradizionale), niente più placeholder "non
            selezionato per l'analisi L3". */}
            <TabPanelStyle value={0}>
              {renderPericolositaEsposizionePanel(
                cdaGraphicalRepresentation,
                overlapCda,
                sormontoIsL3
                  ? sormontoL3DangerFields(overlapCda)
                  : sormontoL2DangerFields(overlapCda),
                sormontoIsL3,
                sormontoIsL3
                  ? undefined
                  : sormontoVulnerabilityFields(overlapCda)
              )}
            </TabPanelStyle>
            {/* value=1: "EROSIONE" (fusa, algoritmo L3 — i 6 campi lista
            cliente di erosionCda.danger, vedi erosioneL3DangerFields.js)
            oppure "EROSIONE GENERALIZZATA" (split, matrice tradizionale
            Ca/Cg, generalizedErosionCda) — cambiano sia i dati che
            l'etichetta della tab sopra, secondo erosionCda.calcMethod. */}
            <TabPanelStyle value={1}>
              {renderPericolositaEsposizionePanel(
                cdaGraphicalRepresentation,
                erosioneIsL3 ? erosionCda : generalizedErosionCda,
                erosioneIsL3
                  ? erosioneL3DangerFields(erosionCda?.danger)
                  : erosioneGeneralizzataDangerFields(generalizedErosionCda),
                erosioneIsL3,
                erosioneIsL3
                  ? undefined
                  : erosioneGeneralizzataVulnerabilityFields(
                      generalizedErosionCda
                    )
              )}
            </TabPanelStyle>
            {!erosioneIsL3 && (
              <TabPanelStyle value={2}>
                {renderPericolositaEsposizionePanel(
                  cdaGraphicalRepresentation,
                  localizedErosionCda,
                  erosioneLocalizzataDangerFields(localizedErosionCda),
                  // localizzata: mostrata solo quando !erosioneIsL3, mai
                  // calcolo L3
                  false,
                  erosioneLocalizzataVulnerabilityFields(localizedErosionCda)
                )}
              </TabPanelStyle>
            )}
          </TabContext>
        </WhitePanel>
      </AccordionDetails>
    </Accordion>
  );
};

const Accordion5CdaL3 = () => {
  // e-2026-06-t21 — stesso pattern di L0/L1, vedi Accordion1GeneralInfoL3.jsx.
  const { inspectionDisabled } = useInspectionSelector();
  const { isViewingInspection } = useInspectionPath();
  const readOnly = inspectionDisabled || isViewingInspection;

  return (
    <Grid container spacing={2} mt={2}>
      <InfoWrapper
        size={3}
        infoDrawer={true}
        drawerTitle={INFO.esitoDellaValutazione.drawerTitle}
        drawerText={INFO.esitoDellaValutazione.drawerText}
      >
        <RHFMultiSelect
          name="esitoDellaValutazione"
          label={'Esito della valutazione'}
          defaultValue={[]}
          selectOptions={EsitoValutazioneL3Options}
          disabled={readOnly}
        />
      </InfoWrapper>

      <InfoWrapper
        size={3}
        infoDrawer={true}
        drawerTitle={INFO.condizioniTransitabilita.drawerTitle}
        drawerText={INFO.condizioniTransitabilita.drawerText}
      >
        <RHFSelect
          name="condizioniTransitabilita"
          label={'Condizioni di transitabilità'}
          defaultValue={''}
          selectOptions={CondizioniTransitabilitaL3Options}
          disabled={readOnly}
        />
      </InfoWrapper>

      <Grid item xs={12} mt={2}>
        <CdaHydraulicSummaryL3 />
      </Grid>
    </Grid>
  );
};

export { Accordion5CdaL3 };
